// People (D114 #6, Console D views/people.js; APS-421): what happened to this person? A person is
// the user.hash the app passes to setUser (spec 11), looked up by it: their sessions, the issues
// they hit, their devices and the requests to their devices (D111), and the people the team pinned
// (shared, with a label the team chose, never a name or an email). The people you viewed lately
// stay on this device, for you only. Owners and admins mark a person as an internal tester (D120,
// APS-547): the list labels them and their page says so. Left out until they are built: the Live
// tab and device commands, a lookup by install id (no read takes one) and Copy for agent (the CLI
// has no users command). The pins and the requests live on the accounts API, which
// the sandbox cannot call, so the sandbox shows the reads only.
import { useQueries, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import {
  appUserPinsQuery,
  appUserQuery,
  appUserSessionsQuery,
  captureSessionsQuery,
  deviceRequestsQuery,
  issuesQuery,
  liveTestersQuery,
  orgQuery,
  sessionCountsQuery,
  useAppUserPin,
  useCancelDeviceRequest,
  useCreateDeviceRequest,
  useLiveTester,
} from '../api/queries'
import type {
  AppUserPin,
  DeviceRequest,
  IssueSummary,
  LiveTester,
  SessionListItem,
} from '../api/types'
import { DeviceData, FAMILY_KEY } from '../components/DeviceData'
import {
  Btn,
  EmptyNote,
  HovRow,
  PageHead,
  Section,
  Strip,
  type StripItem,
  Table,
  TableFoot,
} from '../components/d'
import { Icon } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import {
  CAPTURE_SESSIONS,
  type CaptureDetail,
  type CaptureStatus,
  captureBody,
  captureStatus,
  openCapture,
  REASON_MAX,
  REQUEST_DAYS,
  REQUEST_MIN_USER_HASH,
  reasonProblem,
} from '../lib/capture'
import { day, dayTime, duration, shortId } from '../lib/format'
import { issueTitle, kindTone } from '../lib/issues'
import {
  clearRecent,
  lookupHash,
  type RecentPerson,
  readRecent,
  rememberPerson,
} from '../lib/people'
import { FAMILIES, MAX_OPEN_REQUESTS, type RequestState, requestState } from '../lib/signals'
import { sandboxFor } from '../sandbox'
import { LiveTab, liveUrl } from './PersonLive'
import { useProjectParams } from './params'
import { Outcome } from './Sessions'
import { useWho } from './useWho'

/** The person's sessions and issues: the last 7 days, as the mock's strip counts them. */
const DAYS = 7
/** The devices' window: the app-user read's own (at most 30 days). */
const DEVICE_DAYS = 30
const SESSIONS_SHOWN = 20
const ISSUES_SHOWN = 20
/** Pinned rows read their last session one query each: the first ones, then on demand. */
const PINS_SHOWN = 25

const KIND_LABEL = {
  crash: 'issues.kind.crash',
  error: 'issues.kind.error',
  anr: 'issues.kind.anr',
  hang: 'issues.kind.hang',
  abnormal_exit: 'issues.kind.abnormal_exit',
} as const

/** A hash as a table shows it: whole when short, else its head and tail. */
export function shortHash(hash: string): string {
  return hash.length <= 14 ? hash : `${hash.slice(0, 8)}…${hash.slice(-4)}`
}

/** Two wordings of one label, the short one under 640 px (Mosaic: no cut labels on a phone). */
function Fits({ long, short }: { long: string; short: string }) {
  return (
    <>
      <span className="pp-long">{long}</span>
      <span className="pp-short">{short}</span>
    </>
  )
}

/**
 * A session's path on one line (Mosaic): the Sessions page's SessionPath breaks after each arrow,
 * which a table that scrolls sideways turns into tall rows; here the whole path is the title, and
 * a phone cuts it with an ellipsis.
 */
function PathLine({ screens, more }: { screens: string[]; more: number }) {
  const { t } = useI18n()
  if (screens.length === 0) return '—'
  const extra = more > 0 ? ` ${t('sessions.path.more', { count: more })}` : ''
  return (
    <span className="pp-path" translate="no" title={`${screens.join(' → ')}${extra}`}>
      {screens.map((s, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a path may visit a screen twice
        <span key={i}>
          {i > 0 ? (
            <span className="faint" aria-hidden="true">
              {' → '}
            </span>
          ) : null}
          {s}
        </span>
      ))}
      {extra ? <span className="faint">{extra}</span> : null}
    </span>
  )
}

/** The last session of each hash, one query each, in the same window. */
function useLastSessions(projectId: string, hashes: string[]) {
  return useQueries({
    queries: hashes.map((h) => appUserSessionsQuery(projectId, h, DEVICE_DAYS, 1)),
  })
}

function LastSession({
  orgId,
  projectId,
  session,
}: {
  orgId: string
  projectId: string
  session: SessionListItem | undefined
}) {
  if (!session) return <span className="faint">–</span>
  return (
    <span className="pp-ls">
      <Link
        className="lk"
        translate="no"
        to="/o/$orgId/p/$projectId/sessions/$sessionId"
        params={{ orgId, projectId, sessionId: session.session_id }}
      >
        {shortId(session.session_id)}
      </Link>
      <Outcome outcome={session.outcome} />
    </span>
  )
}

function deviceLine(s: SessionListItem): string {
  return [s.device_model, `${s.os_name} ${s.os_version}`.trim(), s.release]
    .filter(Boolean)
    .join(' · ')
}

export function PeoplePage() {
  const i18n = useI18n()
  const { t, relative, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const sandbox = Boolean(sandboxFor(projectId))
  const inputId = useId()
  const [q, setQ] = useState('')
  const [bad, setBad] = useState(false)
  const [recent, setRecent] = useState<RecentPerson[]>(() => readRecent(projectId))
  const [allPins, setAllPins] = useState(false)
  const pins = useQuery({ ...appUserPinsQuery(orgId, projectId), enabled: !sandbox })
  const pinList = pins.data?.pins ?? []
  const shownPins = allPins ? pinList : pinList.slice(0, PINS_SHOWN)
  const pinLast = useLastSessions(
    projectId,
    shownPins.map((p) => p.user_hash),
  )
  const recentLast = useLastSessions(
    projectId,
    recent.map((r) => r.hash),
  )
  const params = { orgId, projectId }
  const openPerson = (hash: string) =>
    navigate({
      to: '/o/$orgId/p/$projectId/users/$userHash',
      params: { ...params, userHash: hash },
    })

  function lookup(e: FormEvent) {
    e.preventDefault()
    const hash = lookupHash(q)
    setBad(!hash)
    if (hash) openPerson(hash)
  }

  return (
    <main id="main" className="pg pp-pg">
      <PageHead
        title={t('people.title')}
        meta={
          sandbox || !pins.data
            ? t('people.meta.lookup')
            : t('people.meta', { count: pinList.length })
        }
        actions={
          <Link className="btn" to="/o/$orgId/p/$projectId/sessions" params={params}>
            {t('nav.sessions')}
          </Link>
        }
      />
      <form className="pp-find" onSubmit={lookup} noValidate>
        <label className="pp-find-l" htmlFor={inputId}>
          {t('people.lookup.label')}
        </label>
        <div className="pp-box">
          <Icon name="search" />
          <input
            id={inputId}
            name="q"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setBad(false)
            }}
            placeholder={t('people.lookup.placeholder')}
            autoComplete="off"
            spellCheck={false}
            translate="no"
            maxLength={256}
            aria-invalid={bad || undefined}
            aria-describedby={`${inputId}-h`}
          />
          <Btn variant="solid" type="submit">
            {t('people.lookup.go')} <kbd>↵</kbd>
          </Btn>
        </div>
        <p
          id={`${inputId}-h`}
          className={bad ? 'pp-find-h pp-bad' : 'pp-find-h'}
          role={bad ? 'alert' : undefined}
        >
          {bad ? t('people.lookup.bad') : t('people.lookup.hint')}
        </p>
      </form>
      {sandbox ? null : (
        <Section
          title={t('people.pinned.title')}
          meta={pins.data ? t('people.pinned.meta', { count: pinList.length }) : undefined}
        >
          {pins.isPending ? (
            <Loading />
          ) : pins.isError ? (
            <ErrorState error={pins.error} retry={() => pins.refetch()} />
          ) : pinList.length === 0 ? (
            <EmptyNote title={t('people.pinned.none')}>{t('people.pinned.noneHint')}</EmptyNote>
          ) : (
            <>
              <Table label={t('people.table.pinned')} rowKeys>
                <thead>
                  <tr>
                    <th className="f" scope="col">
                      {t('people.col.name')}
                    </th>
                    <th scope="col">{t('people.col.hash')}</th>
                    <th scope="col">{t('people.col.lastSession')}</th>
                    <th scope="col">{t('people.col.lastSeen')}</th>
                    <th className="last" scope="col">
                      {t('people.col.device')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {shownPins.map((p, i) => {
                    const s = pinLast[i]?.data?.sessions[0]
                    return (
                      <HovRow
                        key={p.id}
                        cur={i === 0}
                        label={t('people.open', { name: p.label })}
                        onOpen={() => openPerson(p.user_hash)}
                      >
                        <td className="f">
                          <Link
                            className="ttl"
                            to="/o/$orgId/p/$projectId/users/$userHash"
                            params={{ ...params, userHash: p.user_hash }}
                          >
                            {p.label}
                          </Link>
                          {p.tester ? <span className="t2">{t('people.tester.tag')}</span> : null}
                        </td>
                        <td className="mono" translate="no" title={p.user_hash}>
                          {shortHash(p.user_hash)}
                        </td>
                        <td>
                          <LastSession orgId={orgId} projectId={projectId} session={s} />
                        </td>
                        <td>
                          {s ? (
                            <span
                              title={date(s.last_seen_at, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            >
                              {relative(s.last_seen_at)}
                            </span>
                          ) : pinLast[i]?.isPending ? (
                            ''
                          ) : (
                            <span className="faint">
                              {t('people.notSeen', { days: DEVICE_DAYS })}
                            </span>
                          )}
                        </td>
                        <td className="last" translate="no">
                          {s ? deviceLine(s) : ''}
                        </td>
                      </HovRow>
                    )
                  })}
                </tbody>
              </Table>
              {pinList.length > PINS_SHOWN && !allPins ? (
                <TableFoot>
                  <span>
                    {t('people.pinned.shown', { shown: PINS_SHOWN, count: pinList.length })}
                  </span>
                  <Btn size="sm" onClick={() => setAllPins(true)}>
                    {t('people.pinned.all')}
                  </Btn>
                </TableFoot>
              ) : null}
            </>
          )}
        </Section>
      )}
      <Section title={t('people.recent.title')} meta={t('people.recent.meta')}>
        {recent.length === 0 ? (
          <EmptyNote title={t('people.recent.none')}>{t('people.recent.noneHint')}</EmptyNote>
        ) : (
          <>
            <Table label={t('people.table.recent')} rowKeys={sandbox}>
              <thead>
                <tr>
                  <th className="f" scope="col">
                    {t('people.col.hash')}
                  </th>
                  <th scope="col">{t('people.col.lastSession')}</th>
                  <th scope="col">{t('people.col.lastSeen')}</th>
                  <th scope="col">{t('people.col.device')}</th>
                  <th className="last" scope="col">
                    {t('people.col.viewed')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r, i) => {
                  const s = recentLast[i]?.data?.sessions[0]
                  return (
                    <HovRow
                      key={r.hash}
                      cur={sandbox && i === 0}
                      label={t('people.open', { name: r.hash })}
                      onOpen={() => openPerson(r.hash)}
                    >
                      <td className="f">
                        <Link
                          className="ttl mono"
                          translate="no"
                          title={r.hash}
                          to="/o/$orgId/p/$projectId/users/$userHash"
                          params={{ ...params, userHash: r.hash }}
                        >
                          {shortHash(r.hash)}
                        </Link>
                      </td>
                      <td>
                        <LastSession orgId={orgId} projectId={projectId} session={s} />
                      </td>
                      <td>
                        {s ? (
                          <span
                            title={date(s.last_seen_at, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })}
                          >
                            {relative(s.last_seen_at)}
                          </span>
                        ) : recentLast[i]?.isPending ? (
                          ''
                        ) : (
                          <span className="faint">
                            {t('people.notSeen', { days: DEVICE_DAYS })}
                          </span>
                        )}
                      </td>
                      <td translate="no">{s ? deviceLine(s) : ''}</td>
                      <td className="last">{relative(new Date(r.at))}</td>
                    </HovRow>
                  )
                })}
              </tbody>
            </Table>
            <TableFoot>
              <span>{t('people.recent.foot')}</span>
              <Btn
                size="sm"
                variant="ghost"
                onClick={() => {
                  clearRecent(projectId)
                  setRecent([])
                }}
              >
                {t('people.recent.clear')}
              </Btn>
            </TableFoot>
          </>
        )}
      </Section>
    </main>
  )
}

/** How the last session ended, in the strip's short words (Mosaic): ✓ ended, ✕ crashed. */
const SHORT_OUTCOME = {
  exited: { glyph: '✓', word: 'people.outcome.ended' },
  crashed: { glyph: '✕', word: 'people.outcome.crashed' },
  abnormal_exit: { glyph: '○', word: 'people.outcome.abnormal' },
  active: { glyph: '●', word: 'people.outcome.live' },
  unknown: { glyph: '–', word: 'session.outcome.unknown' },
} as const

function ShortOutcome({ outcome }: { outcome: string }) {
  const { t } = useI18n()
  const key = (outcome in SHORT_OUTCOME ? outcome : 'unknown') as keyof typeof SHORT_OUTCOME
  const o = SHORT_OUTCOME[key]
  return (
    <span className={`us-o ${key}`}>
      <span aria-hidden="true">{o.glyph} </span>
      {t(o.word)}
    </span>
  )
}

/** Mosaic's strip (APS-421): sessions, median session, last seen, crashes, devices. */
function personStrip(
  i18n: Translator,
  c:
    | {
        sessions: number
        crashed: number
        abnormal_exit: number
        with_errors: number
        duration_ms: { p50: number | null; p90: number | null }
      }
    | undefined,
  last: SessionListItem | undefined,
  issues: number | undefined,
  issuesMore: boolean,
  devices: { count: number; more: boolean; newest: string | undefined } | undefined,
): StripItem[] {
  const { t, number, relativeParts, date } = i18n
  const dash = '–'
  const [ago, word] = last ? relativeParts(last.last_seen_at) : [dash, '']
  return [
    {
      dt: t('people.strip.sessions'),
      dd: c ? number(c.sessions) : dash,
      ...(c
        ? { d: t('people.strip.sessionsSub', { abnormal: c.abnormal_exit, errors: c.with_errors }) }
        : {}),
    },
    {
      dt: <Fits long={t('people.strip.median')} short={t('people.strip.medianShort')} />,
      dd: c?.duration_ms.p50 == null ? dash : duration(i18n, c.duration_ms.p50),
      ...(c?.duration_ms.p90 != null
        ? { d: t('people.strip.p90', { length: duration(i18n, c.duration_ms.p90) }) }
        : {}),
    },
    {
      dt: t('people.strip.lastSeen'),
      dd: last ? (
        <span title={date(last.last_seen_at, { dateStyle: 'medium', timeStyle: 'short' })}>
          {ago}
        </span>
      ) : (
        dash
      ),
      ...(last && word ? { small: word } : {}),
      ...(last
        ? {
            d: (
              <>
                <span translate="no">{last.device_model || dash}</span>
                {' · '}
                <ShortOutcome outcome={last.outcome} />
              </>
            ),
          }
        : {}),
    },
    {
      dt: t('people.strip.crashes'),
      dd: c ? number(c.crashed) : dash,
      ...(issues !== undefined
        ? {
            d: issuesMore
              ? t('people.strip.issuesMore', { n: issues })
              : t('people.strip.issues', { count: issues }),
          }
        : {}),
    },
    {
      dt: t('people.strip.devices'),
      dd: devices ? (devices.more ? `${devices.count}+` : number(devices.count)) : dash,
      ...(devices?.newest
        ? { d: <span translate="no">{t('people.strip.newest', { model: devices.newest })}</span> }
        : {}),
    },
  ]
}

export function PersonPage() {
  const i18n = useI18n()
  const { t, number, relative, date } = i18n
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const { userHash = '' } = useParams({ strict: false }) as { userHash?: string }
  const hash = lookupHash(userHash) ?? ''
  const sandbox = Boolean(sandboxFor(projectId))
  const params = { orgId, projectId }
  const valid = hash !== ''

  const devices = useQuery({ ...appUserQuery(projectId, hash), enabled: valid })
  const counts = useQuery({
    ...sessionCountsQuery(projectId, { user: hash, days: DAYS }),
    enabled: valid,
  })
  const sessions = useQuery({
    ...appUserSessionsQuery(projectId, hash, DAYS, SESSIONS_SHOWN),
    enabled: valid,
  })
  const issues = useQuery({
    ...issuesQuery(projectId, {
      user: hash,
      state: 'all',
      sort: 'last_seen',
      days: DAYS,
      limit: ISSUES_SHOWN,
    }),
    enabled: valid,
  })
  const pins = useQuery({ ...appUserPinsQuery(orgId, projectId), enabled: valid && !sandbox })
  const org = useQuery({ ...orgQuery(orgId), enabled: !sandbox })
  const canEdit = org.data ? org.data.role === 'owner' || org.data.role === 'admin' : undefined
  const pin = pins.data?.pins.find((p) => p.user_hash === hash)
  const who = useWho(orgId, !sandbox)
  const testers = useQuery({
    ...liveTestersQuery(orgId, projectId, hash),
    enabled: valid && !sandbox,
  })
  const tester = currentTester(testers.data)
  const pinFormId = useId()
  const capFormId = useId()
  const testerFormId = useId()
  // One form under the head at a time: opening one closes the others (the mock's `win`).
  const [form, setForm] = useState<'pin' | 'capture' | 'tester' | null>(null)
  const pinOpen = form === 'pin'
  const capOpen = form === 'capture'
  const testerOpen = form === 'tester'
  // Too short for a request (APS-541): Capture and Device data stay off, and a note says why.
  const shortHashed = hash.length < REQUEST_MIN_USER_HASH
  // Too short to be a tester (APS-547): the same kind of note, in the same place.
  const testerShort = hash.length < TESTER_MIN_HASH && !tester
  // Overview and Live (D120): Live opens a live view, so only a current tester's, on a build
  // that knows the live Worker, outside the sandbox.
  const search = useSearch({ strict: false }) as { tab?: 'live' }
  const tabs = !sandbox && liveUrl() !== ''
  const liveOpen = tabs && search.tab === 'live' && Boolean(tester)
  // Only an open live view knows a device is online: the dot shows while it does.
  const [liveOnline, setLiveOnline] = useState(false)
  const openTab = (tab: 'live' | undefined) =>
    navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, tab }),
      replace: true,
    })
  const requests = useQuery({
    ...deviceRequestsQuery(orgId, projectId, hash),
    enabled: valid && !sandbox,
  })
  const newest = [...(requests.data ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at))
  const captures = newest.filter((r) => r.kind === 'capture')
  // Sends and deletes; a capture is listed once, in Capture requests (Mosaic, APS-548).
  const asks = newest.filter((r) => r.kind !== 'capture')

  useEffect(() => {
    if (valid) rememberPerson(projectId, hash)
  }, [projectId, hash, valid])

  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/users" params={params}>
        {t('people.title')}
      </Link>
      {' / '}
      <span translate="no">{shortHash(userHash)}</span>
    </>
  )
  const title = (
    <>
      <span translate="no">{shortHash(userHash)}</span>
      {pin ? <span className="pp-dn">{pin.label}</span> : null}
    </>
  )

  if (!valid)
    return (
      <main id="main" className="pg pp-pg">
        <PageHead crumb={crumb} title={title} variant="mono" />
        <Section title={t('people.invalid.title')}>
          <EmptyNote title={t('people.invalid.body')}>
            <Link className="btn" to="/o/$orgId/p/$projectId/users" params={params}>
              {t('people.back')}
            </Link>
          </EmptyNote>
        </Section>
      </main>
    )

  const deviceList = devices.data?.devices ?? []
  const lastSession = sessions.data?.sessions[0]
  const firstSeen = deviceList.reduce<string | undefined>(
    (min, d) => (min === undefined || d.first_seen < min ? d.first_seen : min),
    undefined,
  )
  const unknown = devices.isSuccess && sessions.isSuccess && deviceList.length === 0 && !lastSession
  const issueList = issues.data?.issues ?? []
  const meta = [
    <span key="hash" translate="no">{`user.hash ${hash}`}</span>,
    firstSeen ? t('people.meta.first', { day: day(i18n, firstSeen) }) : null,
    devices.data ? t('people.meta.devices', { count: deviceList.length, days: DEVICE_DAYS }) : null,
    sandbox
      ? null
      : pin
        ? who(pin.created_by)
          ? t('people.meta.pinnedBy', { name: who(pin.created_by) ?? '' })
          : t('people.meta.pinned')
        : pins.data
          ? t('people.meta.notPinned')
          : null,
  ].filter(Boolean)

  return (
    <main id="main" className="pg pp-pg">
      <PageHead
        crumb={crumb}
        title={title}
        variant="mono"
        meta={meta.flatMap((m, i) => (i ? [' · ', m] : [m]))}
        actions={
          sandbox ? null : (
            <>
              <Btn
                data-pp-open="pin"
                disabled={!pins.isSuccess}
                aria-expanded={pinOpen}
                aria-controls={pinOpen ? pinFormId : undefined}
                onClick={() => setForm(pinOpen ? null : 'pin')}
              >
                <Icon name="pin" />
                {pin ? t('people.pin.pinned') : t('people.pin.open')}
              </Btn>
              <Btn
                variant="solid"
                data-pp-open="capture"
                disabled={!canEdit || shortHashed}
                title={canEdit === false && !shortHashed ? t('settings.adminOnly') : undefined}
                aria-expanded={capOpen}
                aria-controls={capOpen ? capFormId : undefined}
                onClick={() => setForm(capOpen ? null : 'capture')}
              >
                <Icon name="capture" />
                {t('people.capture.open')}
              </Btn>
              <Btn
                data-pp-open="tester"
                disabled={!canEdit || !testers.isSuccess || testerShort}
                title={canEdit === false && !testerShort ? t('settings.adminOnly') : undefined}
                aria-expanded={testerOpen}
                aria-controls={testerOpen ? testerFormId : undefined}
                onClick={() => setForm(testerOpen ? null : 'tester')}
              >
                <Icon name="user" />
                {tester ? t('people.tester.is') : t('people.tester.open')}
              </Btn>
              <DeviceData
                orgId={orgId}
                projectId={projectId}
                user={hash}
                canEdit={canEdit}
                label={t('people.deviceData')}
              />
              <Link
                className="btn danger"
                to="/o/$orgId/p/$projectId/settings/$section"
                params={{ ...params, section: 'privacy' }}
              >
                {t('people.erase')}
              </Link>
            </>
          )
        }
      />
      {(shortHashed || testerShort) && !sandbox ? (
        <p className="note pp-hash-note">
          {[
            shortHashed ? t('people.capture.shortHash', { n: REQUEST_MIN_USER_HASH }) : null,
            testerShort ? t('people.tester.short', { min: TESTER_MIN_HASH }) : null,
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      ) : null}
      {pinOpen && !sandbox ? (
        <PinForm
          id={pinFormId}
          orgId={orgId}
          projectId={projectId}
          hash={hash}
          pin={pin}
          onClose={() => setForm(null)}
        />
      ) : null}
      {capOpen && !sandbox ? (
        <CaptureForm
          id={capFormId}
          orgId={orgId}
          projectId={projectId}
          hash={hash}
          pending={openCapture(captures)}
          onClose={() => setForm(null)}
        />
      ) : null}
      {testerOpen && !sandbox ? (
        <TesterForm
          id={testerFormId}
          orgId={orgId}
          projectId={projectId}
          hash={hash}
          tester={tester}
          onClose={() => setForm(null)}
        />
      ) : null}
      {tester && !sandbox ? (
        <p className="pp-consent">
          <Icon name="user" />
          <span>
            <b>{t('people.tester.banner')}</b>{' '}
            {who(tester.created_by)
              ? t('people.tester.bannerBy', {
                  name: who(tester.created_by) ?? '',
                  from: day(i18n, tester.created_at),
                  until: day(i18n, tester.expires_at),
                })
              : t('people.tester.bannerSince', {
                  from: day(i18n, tester.created_at),
                  until: day(i18n, tester.expires_at),
                })}{' '}
            {t('people.tester.bannerLive')}
          </span>
        </p>
      ) : null}
      {tabs ? (
        <div className="tabs pp-tabs" role="tablist" aria-label={t('people.tabs')}>
          <button
            type="button"
            role="tab"
            id="pp-tab-ov"
            aria-selected={!liveOpen}
            aria-controls="pp-panel"
            onClick={() => openTab(undefined)}
          >
            {t('people.tab.overview')}
          </button>
          <button
            type="button"
            role="tab"
            id="pp-tab-live"
            aria-selected={liveOpen}
            aria-controls="pp-panel"
            disabled={!tester}
            title={tester ? undefined : t('people.tab.liveOff')}
            onClick={() => openTab('live')}
          >
            {t('people.tab.live')}
            {liveOnline ? (
              <span className="n">
                <span className="pp-on">{t('people.tab.online')}</span>
              </span>
            ) : null}
          </button>
        </div>
      ) : null}
      <TabPanel tabs={tabs} labelledBy={liveOpen ? 'pp-tab-live' : 'pp-tab-ov'}>
        {liveOpen && tester ? (
          // A tester's own view: another person's page opens its own ticket and socket (Loom).
          <LiveTab
            key={tester.id}
            projectId={projectId}
            tester={tester}
            canEdit={canEdit}
            onOnline={setLiveOnline}
          />
        ) : unknown ? (
          <Section title={t('people.unknown.title', { days: DEVICE_DAYS })}>
            <EmptyNote title={t('people.unknown.body')}>
              <span>{t('people.unknown.hint')}</span>
              <span className="pp-acts">
                <Link className="btn" to="/o/$orgId/p/$projectId/users" params={params}>
                  {t('people.back')}
                </Link>
                <Link className="btn" to="/o/$orgId/p/$projectId/sessions" params={params}>
                  {t('nav.sessions')}
                </Link>
              </span>
            </EmptyNote>
          </Section>
        ) : (
          <>
            <Strip
              label={t('people.strip.label')}
              items={personStrip(
                i18n,
                counts.data,
                lastSession,
                issues.data ? issueList.length : undefined,
                Boolean(issues.data?.next_cursor),
                devices.data
                  ? {
                      count: deviceList.length,
                      more: devices.data.more,
                      newest: deviceList[0]?.device_model || undefined,
                    }
                  : undefined,
              )}
            />
            <Section
              className="pp-sess"
              title={t('nav.sessions')}
              meta={
                counts.data ? (
                  <Fits
                    long={t('people.sessions.meta', {
                      shown: sessions.data?.sessions.length ?? 0,
                      count: counts.data.sessions,
                      days: DAYS,
                    })}
                    short={t('people.sessions.metaShort', {
                      shown: sessions.data?.sessions.length ?? 0,
                      count: counts.data.sessions,
                      days: DAYS,
                    })}
                  />
                ) : undefined
              }
              right={
                <Link
                  className="btn ghost sm"
                  to="/o/$orgId/p/$projectId/sessions"
                  params={params}
                  search={{ user: hash }}
                >
                  {t('people.sessions.all')}
                </Link>
              }
            >
              {sessions.isPending ? (
                <Loading />
              ) : sessions.isError ? (
                <ErrorState error={sessions.error} retry={() => sessions.refetch()} />
              ) : sessions.data.sessions.length === 0 ? (
                <EmptyNote title={t('people.sessions.none', { days: DAYS })} />
              ) : (
                <Table label={t('people.table.sessions')} rowKeys>
                  <thead>
                    <tr>
                      <th className="f" scope="col">
                        {t('sessions.col.session')}
                      </th>
                      <th scope="col">{t('sessions.col.outcome')}</th>
                      <th scope="col">{t('sessions.col.started')}</th>
                      <th className="num" scope="col">
                        {t('sessions.col.length')}
                      </th>
                      <th scope="col">{t('releases.col.release')}</th>
                      <th scope="col">{t('sessions.col.device')}</th>
                      <th className="last" scope="col">
                        {t('sessions.col.path')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.data.sessions.map((s, i) => (
                      <HovRow
                        key={`${s.app_id}-${s.session_id}`}
                        cur={i === 0}
                        onOpen={() =>
                          navigate({
                            to: '/o/$orgId/p/$projectId/sessions/$sessionId',
                            params: { ...params, sessionId: s.session_id },
                          })
                        }
                      >
                        <td className="f">
                          <Link
                            className="ttl"
                            translate="no"
                            to="/o/$orgId/p/$projectId/sessions/$sessionId"
                            params={{ ...params, sessionId: s.session_id }}
                          >
                            {shortId(s.session_id)}
                          </Link>
                          {s.capture_requests.length > 0 ? (
                            <span className="pp-cap-i" title={t('people.capture.onRequest')}>
                              <Icon name="capture" small />
                              <span className="visually-hidden">
                                {t('people.capture.onRequest')}
                              </span>
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <Outcome outcome={s.outcome} />
                        </td>
                        <td>
                          <span
                            title={date(s.started_at, { dateStyle: 'medium', timeStyle: 'short' })}
                          >
                            {relative(s.started_at)}
                          </span>
                        </td>
                        <td className="num">{duration(i18n, Math.max(0, s.duration_ms))}</td>
                        <td translate="no">{s.release}</td>
                        <td translate="no">{s.device_model || '–'}</td>
                        <td className="last">
                          <PathLine screens={s.path.screens} more={s.path.more} />
                        </td>
                      </HovRow>
                    ))}
                  </tbody>
                </Table>
              )}
            </Section>
            <div className="grid2">
              <div className="stack">
                <Section
                  title={t('people.issues.title')}
                  meta={
                    issues.data
                      ? t('people.issues.meta', { count: issueList.length, days: DAYS })
                      : undefined
                  }
                >
                  {issues.isPending ? (
                    <Loading />
                  ) : issues.isError ? (
                    <ErrorState error={issues.error} retry={() => issues.refetch()} />
                  ) : issueList.length === 0 ? (
                    <EmptyNote title={t('people.issues.none', { days: DAYS })}>
                      {t('people.issues.noneHint')}
                    </EmptyNote>
                  ) : (
                    <IssuesTable issues={issueList} />
                  )}
                </Section>
                <Section
                  title={t('people.devices.title')}
                  meta={
                    devices.data
                      ? t('people.devices.meta', { count: deviceList.length, days: DEVICE_DAYS })
                      : undefined
                  }
                >
                  {devices.isPending ? (
                    <Loading />
                  ) : devices.isError ? (
                    <ErrorState error={devices.error} retry={() => devices.refetch()} />
                  ) : (
                    <>
                      <Table label={t('people.table.devices')}>
                        <thead>
                          <tr>
                            <th className="f" scope="col">
                              {t('people.col.model')}
                            </th>
                            <th scope="col">{t('sessions.col.os')}</th>
                            <th scope="col">{t('people.col.install')}</th>
                            <th scope="col">{t('releases.col.release')}</th>
                            <th className="num" scope="col">
                              {t('nav.sessions')}
                            </th>
                            <th scope="col">{t('people.col.firstSeen')}</th>
                            <th className="last" scope="col">
                              {t('people.col.lastSeen')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceList.map((d) => (
                            <tr key={d.installation_id}>
                              <td className="f" translate="no">
                                {d.device_model || '–'}
                              </td>
                              <td translate="no">{`${d.os_name} ${d.os_version}`.trim() || '–'}</td>
                              <td className="mono" translate="no" title={d.installation_id}>
                                {shortId(d.installation_id)}
                              </td>
                              <td translate="no">{d.release}</td>
                              <td className="num">{number(d.sessions)}</td>
                              <td>{day(i18n, d.first_seen)}</td>
                              <td className="last">{relative(d.last_seen)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {devices.data.more ? (
                        <TableFoot>
                          <span>{t('people.devices.more', { count: deviceList.length })}</span>
                        </TableFoot>
                      ) : null}
                      <p className="note pp-where">{t('people.devices.where')}</p>
                    </>
                  )}
                </Section>
              </div>
              <div className="stack">
                {sandbox ? null : (
                  <CaptureRequests
                    projectId={projectId}
                    canEdit={canEdit === true}
                    captures={captures}
                    loading={requests.isPending}
                    error={requests.error}
                    retry={() => requests.refetch()}
                    who={who}
                  />
                )}
                {sandbox ? null : (
                  <DeviceRequests
                    projectId={projectId}
                    user={hash}
                    canEdit={canEdit}
                    requests={asks}
                    loading={requests.isPending}
                    error={requests.error}
                    retry={() => requests.refetch()}
                    who={who}
                  />
                )}
                <Section title={t('people.setUser.title')}>
                  <dl className="kv pp-kv">
                    <dt translate="no">user.hash</dt>
                    <dd translate="no">{hash}</dd>
                  </dl>
                </Section>
              </div>
            </div>
          </>
        )}
      </TabPanel>
    </main>
  )
}

function IssuesTable({ issues }: { issues: IssueSummary[] }) {
  const { t, number, relative } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const navigate = useNavigate()
  const params = { orgId, projectId }
  return (
    <Table label={t('people.table.issues')}>
      <thead>
        <tr>
          <th className="f" scope="col">
            {t('people.col.issue')}
          </th>
          <th scope="col">{t('people.col.kind')}</th>
          <th className="num" scope="col">
            {t('people.col.theirSessions')}
          </th>
          <th className="last" scope="col">
            {t('people.col.lastInTheirs')}
          </th>
        </tr>
      </thead>
      <tbody>
        {issues.map((x) => (
          <HovRow
            key={x.id}
            onOpen={() =>
              navigate({
                to: '/o/$orgId/p/$projectId/issues/$issueId',
                params: { ...params, issueId: x.id },
              })
            }
          >
            <td className="f wrap">
              <Link
                className="ttl"
                translate="no"
                to="/o/$orgId/p/$projectId/issues/$issueId"
                params={{ ...params, issueId: x.id }}
              >
                {issueTitle(x)}
              </Link>
              {x.state?.status === 'resolved' || x.state?.status === 'ignored' ? (
                <span className="t2">
                  {t(
                    x.state.status === 'resolved'
                      ? 'people.issue.resolved'
                      : 'people.issue.ignored',
                  )}
                </span>
              ) : null}
            </td>
            <td>
              <span className={`pill ${kindTone(x.kind)}`}>{t(KIND_LABEL[x.kind])}</span>
            </td>
            <td className="num">{number(x.sessions)}</td>
            <td className="last">{relative(x.last_seen)}</td>
          </HovRow>
        ))}
      </tbody>
    </Table>
  )
}

/** The pin's message for an error: taken (409), refused or at the cap (400), or the general one. */
function pinError(t: Translator['t'], error: unknown): string {
  if (isApiError(error, 409)) return t('people.pin.taken')
  if (isApiError(error, 400)) return t('people.pin.refused')
  if (isApiError(error, 403)) return t('settings.adminOnly')
  return t('state.failed')
}

/** Pin for the team, or rename and unpin: a form under the head, as the mock's `win`. */
function PinForm({
  id,
  orgId,
  projectId,
  hash,
  pin,
  onClose,
}: {
  id: string
  orgId: string
  projectId: string
  hash: string
  pin: AppUserPin | undefined
  onClose: () => void
}) {
  const { t } = useI18n()
  const [label, setLabel] = useState(pin?.label ?? '')
  const { pin: create, rename, unpin } = useAppUserPin(orgId, projectId)
  const busy = create.isPending || rename.isPending || unpin.isPending
  const error = create.error ?? rename.error ?? unpin.error
  const value = label.trim()
  const title = pin ? t('people.pin.rename') : t('people.pin.title')

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!value) return
    const done = { onSuccess: onClose }
    if (pin) rename.mutate({ pinId: pin.id, label: value }, done)
    else create.mutate({ user_hash: hash, label: value }, done)
  }

  return (
    <form id={id} className="win pp-form" onSubmit={submit} aria-label={title}>
      <div className="win-bar">
        <b>{title}</b>
        <span className="pp-sub">{t('people.pin.sub')}</span>
        <span className="r">
          <Btn size="sm" variant="ghost" aria-label={t('people.pin.cancel')} onClick={onClose}>
            <Icon name="x" small />
          </Btn>
        </span>
      </div>
      <div className="pp-ff">
        <label className="lbl pp-wide">
          {t('people.pin.label')}
          <input
            className="field"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('people.pin.placeholder')}
            maxLength={60}
            required
            // biome-ignore lint/a11y/noAutofocus: the form opens on the button's press, for typing
            autoFocus
          />
        </label>
      </div>
      <div className="pp-ff-f">
        <span className="note">{t('people.pin.note')}</span>
        <span className="pp-r">
          {pin ? (
            <Btn
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => unpin.mutate(pin.id, { onSuccess: onClose })}
            >
              {t('people.pin.unpin')}
            </Btn>
          ) : null}
          <Btn size="sm" variant="ghost" onClick={onClose}>
            {t('people.pin.cancel')}
          </Btn>
          <Btn size="sm" variant="solid" type="submit" disabled={busy || !value}>
            {pin ? t('people.pin.renameGo') : t('people.pin.go')}
          </Btn>
        </span>
      </div>
      {error ? (
        <p role="alert" className="notice error pp-err">
          {pinError(t, error)}
        </p>
      ) : null}
    </form>
  )
}

/** The person's content: the tabs' panel when there are tabs, else the page's own grid. */
function TabPanel({
  tabs,
  labelledBy,
  children,
}: {
  tabs: boolean
  labelledBy: string
  children: ReactNode
}) {
  return tabs ? (
    <div className="pp-pane" id="pp-panel" role="tabpanel" aria-labelledby={labelledBy}>
      {children}
    </div>
  ) : (
    <div className="pp-pane">{children}</div>
  )
}

/** A user hash marks a tester from this many characters (the API's LIVE_TESTER_MIN_HASH). */
const TESTER_MIN_HASH = 16
/** How long a tester lasts, in days: the API takes 1 to 365 and defaults to 90 (D120). */
const TESTER_DAYS = ['30', '90', '180', '365'] as const

/** The app user's current tester row, the latest to expire (rows come newest first). */
export function currentTester(rows: LiveTester[] | undefined): LiveTester | undefined {
  return (rows ?? [])
    .filter((r) => r.state === 'current')
    .reduce<LiveTester | undefined>(
      (a, r) => (!a || r.expires_at > a.expires_at ? r : a),
      undefined,
    )
}

/**
 * Mark an app user as an internal tester (D120), or remove one: a form under the head, as the
 * mock's `win`. Owners and admins; the API keeps only the hash's last four characters.
 */
function TesterForm({
  id,
  orgId,
  projectId,
  hash,
  tester,
  onClose,
}: {
  id: string
  orgId: string
  projectId: string
  hash: string
  tester: LiveTester | undefined
  onClose: () => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { add, remove } = useLiveTester(orgId, projectId)
  const [days, setDays] = useState<(typeof TESTER_DAYS)[number]>('90')
  const busy = add.isPending || remove.isPending
  const error = add.error ?? remove.error
  const title = tester ? t('people.tester.is') : t('people.tester.title')

  function submit(e: FormEvent) {
    e.preventDefault()
    if (tester) remove.mutate(tester.id, { onSuccess: onClose })
    else add.mutate({ user: hash, days: Number(days) }, { onSuccess: onClose })
  }

  return (
    <form id={id} className="win pp-form" onSubmit={submit} aria-label={title}>
      <div className="win-bar">
        <b>{title}</b>
        <span className="pp-sub">{t('people.tester.sub', { hash: shortHash(hash) })}</span>
        <span className="r">
          <Btn size="sm" variant="ghost" aria-label={t('people.pin.cancel')} onClick={onClose}>
            <Icon name="x" small />
          </Btn>
        </span>
      </div>
      <div className="pp-ff">
        {tester ? (
          <p className="pp-wide">
            {t('people.tester.until', { until: dayTime(i18n, tester.expires_at) })}
          </p>
        ) : (
          <label className="lbl">
            {t('people.tester.for')}
            <select
              className="select"
              value={days}
              onChange={(e) => setDays(e.target.value as (typeof TESTER_DAYS)[number])}
              // biome-ignore lint/a11y/noAutofocus: the form opens on the button's press
              autoFocus
            >
              {TESTER_DAYS.map((d) => (
                <option key={d} value={d}>
                  {t('device.daysN', { n: Number(d) })}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="pp-ff-f">
        <span className="note">
          {tester ? t('people.tester.noteRemove') : t('people.tester.note')}
        </span>
        <span className="pp-r">
          <Btn size="sm" variant="ghost" onClick={onClose}>
            {t('people.pin.cancel')}
          </Btn>
          <Btn size="sm" variant="solid" type="submit" disabled={busy}>
            {tester ? t('people.tester.remove') : t('people.tester.go')}
          </Btn>
        </span>
      </div>
      {error ? (
        <p role="alert" className="notice error pp-err">
          {isApiError(error, 403)
            ? t('settings.adminOnly')
            : isApiError(error, 409)
              ? t('people.tester.conflict')
              : isApiError(error, 400)
                ? t('people.tester.short', { min: TESTER_MIN_HASH })
                : isApiError(error, 404)
                  ? t('people.tester.gone')
                  : t('state.failed')}
        </p>
      ) : null}
    </form>
  )
}

/** A capture request's state word, the console's glyph and tone for it. */
const CAPTURE_WORD = {
  pending: { glyph: '○', cls: 'on', key: 'people.capture.state.pending' },
  capturing: { glyph: '●', cls: 'on', key: 'people.capture.state.capturing' },
  captured: { glyph: '✓', cls: 'ok', key: 'people.capture.state.captured' },
  expired: { glyph: '×', cls: 'mute', key: 'people.capture.state.expired' },
  replaced: { glyph: '–', cls: 'mute', key: 'people.capture.state.replaced' },
  canceled: { glyph: '–', cls: 'mute', key: 'people.capture.state.canceled' },
} as const satisfies Record<CaptureStatus, { glyph: string; cls: string; key: MessageId }>

/** Capture an app user's next sessions in full (spec 15.5): a form under the head, as the mock's `win`. */
function CaptureForm({
  id,
  orgId,
  projectId,
  hash,
  pending,
  onClose,
}: {
  id: string
  orgId: string
  projectId: string
  hash: string
  /** The person's open capture, which this one replaces. */
  pending: DeviceRequest | undefined
  onClose: () => void
}) {
  const i18n = useI18n()
  const { t } = i18n
  const create = useCreateDeviceRequest(orgId, projectId)
  const [sessions, setSessions] = useState(String(CAPTURE_SESSIONS.default))
  const [days, setDays] = useState<(typeof REQUEST_DAYS)[number]>('7')
  const [detail, setDetail] = useState<CaptureDetail>('replay')
  const [notify, setNotify] = useState(true)
  const [reason, setReason] = useState('')
  const n = Number(sessions)
  const sessionsOk = Number.isInteger(n) && n >= CAPTURE_SESSIONS.min && n <= CAPTURE_SESSIONS.max
  const problem = reasonProblem(reason)
  const title = t('people.capture.title')

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!sessionsOk || problem) return
    create.mutate(captureBody({ hash, sessions: n, days: Number(days), reason, detail, notify }), {
      onSuccess: onClose,
    })
  }

  return (
    <form id={id} className="win pp-form" onSubmit={submit} aria-label={title}>
      <div className="win-bar">
        <b>{title}</b>
        <span className="pp-sub">{t('people.capture.sub', { hash: shortHash(hash) })}</span>
        <span className="r">
          <Btn size="sm" variant="ghost" aria-label={t('people.pin.cancel')} onClick={onClose}>
            <Icon name="x" small />
          </Btn>
        </span>
      </div>
      <div className="pp-ff">
        <label className="lbl">
          {t('people.capture.sessions')}
          <input
            className="input mono"
            type="number"
            inputMode="numeric"
            min={CAPTURE_SESSIONS.min}
            max={CAPTURE_SESSIONS.max}
            value={sessions}
            onChange={(e) => setSessions(e.target.value)}
            required
            // biome-ignore lint/a11y/noAutofocus: the form opens on the button's press, for typing
            autoFocus
          />
        </label>
        <label className="lbl">
          {t('people.capture.expires')}
          <select
            className="select"
            value={days}
            onChange={(e) => setDays(e.target.value as (typeof REQUEST_DAYS)[number])}
          >
            {REQUEST_DAYS.map((d) => (
              <option key={d} value={d}>
                {d === '1' ? t('people.capture.hours24') : t('device.daysN', { n: Number(d) })}
              </option>
            ))}
          </select>
        </label>
        <div className="pp-fld">
          <label className="lbl">
            {t('people.capture.detail')}
            <select
              className="select"
              value={detail}
              onChange={(e) => setDetail(e.target.value as CaptureDetail)}
              aria-describedby={`${id}-detail`}
            >
              <option value="replay">{t('people.capture.detail.replay')}</option>
              <option value="noReplay">{t('people.capture.detail.noReplay')}</option>
            </select>
          </label>
          <span className="note" id={`${id}-detail`}>
            {t('people.capture.detailNote')}
          </span>
        </div>
        <label className="lbl pp-wide">
          {t('device.reason')}
          <input
            className="input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('device.reasonPlaceholder')}
            maxLength={REASON_MAX}
            required
          />
        </label>
        <label className="se-chk pp-wide">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          {t('people.capture.notify')}
        </label>
      </div>
      <div className="pp-ff-f">
        <span className="note">
          {problem === 'email' ? t('device.reasonEmail') : t('device.reasonNote')}
          {pending
            ? ` ${t('people.capture.replaces', { when: dayTime(i18n, pending.created_at) })}`
            : null}
        </span>
        <span className="pp-r">
          <Btn size="sm" variant="ghost" onClick={onClose}>
            {t('people.pin.cancel')}
          </Btn>
          <Btn
            size="sm"
            variant="solid"
            type="submit"
            disabled={create.isPending || !sessionsOk || Boolean(problem)}
          >
            {t('people.capture.go')}
          </Btn>
        </span>
      </div>
      {create.error ? (
        <p role="alert" className="notice error pp-err">
          {isApiError(create.error, 403)
            ? t('settings.adminOnly')
            : isApiError(create.error, 409)
              ? t('device.tooMany', { max: MAX_OPEN_REQUESTS })
              : isApiError(create.error, 400)
                ? t('people.capture.refused')
                : t('state.failed')}
        </p>
      ) : null}
    </form>
  )
}

/** The person's capture requests, newest first, as the mock's side section. */
function CaptureRequests({
  projectId,
  canEdit,
  captures,
  loading,
  error,
  retry,
  who,
}: {
  projectId: string
  /** Owners and admins may cancel a pending or capturing request. */
  canEdit: boolean
  captures: readonly DeviceRequest[]
  loading: boolean
  error: unknown
  retry: () => void
  who: (id: string | null) => string | undefined
}) {
  const { t } = useI18n()
  const { orgId } = useProjectParams()
  const cancel = useCancelDeviceRequest(orgId, projectId)
  return (
    <Section
      title={t('people.capture.requests')}
      meta={loading || error ? undefined : String(captures.length)}
      right={
        <Link
          className="btn ghost sm"
          to="/o/$orgId/p/$projectId/settings/$section"
          params={{ orgId, projectId, section: 'activity' }}
        >
          {t('people.capture.activity')}
        </Link>
      }
    >
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={retry} />
      ) : captures.length === 0 ? (
        <EmptyNote title={t('people.capture.none')}>{t('people.capture.noneBody')}</EmptyNote>
      ) : (
        <ul className="pp-caps">
          {captures.map((r) => (
            <CaptureItem
              key={r.id}
              projectId={projectId}
              request={r}
              by={who(r.created_by)}
              {...(canEdit
                ? { onCancel: () => cancel.mutate(r.id), canceling: cancel.isPending }
                : {})}
            />
          ))}
        </ul>
      )}
    </Section>
  )
}

function CaptureItem({
  projectId,
  request: r,
  by,
  onCancel,
  canceling,
}: {
  projectId: string
  request: DeviceRequest
  by: string | undefined
  /** Cancels it, for an owner or admin; absent otherwise. */
  onCancel?: () => void
  canceling?: boolean
}) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId } = useProjectParams()
  const status = captureStatus(r)
  const word = CAPTURE_WORD[status]
  const of = r.sessions ?? 0
  const captured = r.answers?.captured ?? 0
  const sessions = useQuery({
    ...captureSessionsQuery(projectId, r.id, Math.max(1, of)),
    enabled: captured > 0,
  })
  const rows = sessions.data?.sessions ?? []
  return (
    <li>
      <div className="pp-ch">
        <span className={`st ${word.cls}`}>
          <span aria-hidden="true">{`${word.glyph} `}</span>
          {t(
            // Expired or canceled after some sessions: what it captured, as captured says it
            // (Mosaic, APS-541).
            captured > 0 && (status === 'expired' || status === 'canceled')
              ? (`${word.key}N` as MessageId)
              : word.key,
            { n: captured, of },
          )}
        </span>
        <time dateTime={r.created_at}>{dayTime(i18n, r.created_at)}</time>
        {onCancel && (status === 'pending' || status === 'capturing') ? (
          <Btn size="sm" variant="ghost" onClick={onCancel} disabled={canceling}>
            {t('people.capture.cancel')}
          </Btn>
        ) : null}
      </div>
      <p className="pp-why">{r.reason}</p>
      {by ? (
        <p>
          {t('people.capture.by', { name: by })}
          {r.notify ? ` · ${t('people.capture.emails', { name: by })}` : null}
        </p>
      ) : null}
      {rows.length > 0 ? (
        <p className="mono">
          <span aria-hidden="true">{'→ '}</span>
          {rows.map((s, i) => (
            <span key={s.session_id}>
              {i ? ' · ' : null}
              <Link
                className="ul"
                translate="no"
                to="/o/$orgId/p/$projectId/sessions/$sessionId"
                params={{ orgId, projectId, sessionId: s.session_id }}
              >
                {shortId(s.session_id)}
              </Link>
            </span>
          ))}
        </p>
      ) : null}
    </li>
  )
}

/** A send or delete request's state, in Signals' words; expired as Capture requests marks it. */
const REQUEST_WORD = {
  open: { glyph: '○', cls: 'on', key: 'signals.state.open' },
  delivered: { glyph: '✓', cls: 'ok', key: 'people.requests.delivered' },
  expired: { glyph: '×', cls: 'mute', key: 'signals.state.expired' },
  canceled: { glyph: '–', cls: 'mute', key: 'signals.state.canceled' },
  replaced: { glyph: '–', cls: 'mute', key: 'signals.state.replaced' },
} as const satisfies Record<RequestState, { glyph: string; cls: string; key: MessageId }>

/**
 * This person's send and delete requests (APS-548, Mosaic's A): a list like Capture requests, in
 * the right column. Each one says its state (Cancel on an open one, for owners and admins), what
 * it asks and who asked; Signals keeps the project's table.
 */
function DeviceRequests({
  projectId,
  user,
  canEdit,
  requests,
  loading,
  error,
  retry,
  who,
}: {
  projectId: string
  user: string
  /** Undefined while the viewer's role loads. */
  canEdit: boolean | undefined
  requests: readonly DeviceRequest[]
  loading: boolean
  error: unknown
  retry: () => void
  who: (id: string | null) => string | undefined
}) {
  const i18n = useI18n()
  const { t, locale } = i18n
  const { orgId } = useProjectParams()
  const cancel = useCancelDeviceRequest(orgId, projectId)
  const open = requests.filter((r) => r.state === 'open').length
  // "logs, spans and replay", or everything when it covers every family.
  const what = (r: DeviceRequest) => {
    const families = r.families ?? []
    const all = r.families === null || FAMILIES.every((f) => families.includes(f))
    return t(r.kind === 'delete' ? 'people.requests.delete' : 'people.requests.send', {
      what: all
        ? t('people.requests.everything')
        : // No comma before "and", as the copy writes lists ("logs, spans and replay").
          new Intl.ListFormat(locale === 'en' ? 'en-GB' : locale, { type: 'conjunction' }).format(
            FAMILIES.filter((f) => families.includes(f)).map((f) =>
              t(FAMILY_KEY[f]).toLocaleLowerCase(locale),
            ),
          ),
    })
  }
  return (
    <Section
      title={t('signals.requests')}
      meta={loading || error ? undefined : t('people.requestsSub', { open })}
      right={
        <DeviceData
          orgId={orgId}
          projectId={projectId}
          user={user}
          canEdit={canEdit}
          label={t('signals.newRequest')}
        />
      }
    >
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} retry={retry} />
      ) : requests.length === 0 ? (
        <p className="dim cr-empty">{t('people.noRequests')}</p>
      ) : (
        <ul className="pp-caps">
          {requests.map((r) => {
            const state = requestState(r)
            const word = REQUEST_WORD[state]
            const by = who(r.created_by)
            return (
              <li key={r.id}>
                <div className="pp-ch">
                  <span className={`st ${word.cls}`}>
                    <span aria-hidden="true">{`${word.glyph} `}</span>
                    {t(word.key, { n: r.answers?.devices ?? 0 })}
                  </span>
                  <time dateTime={r.created_at}>{dayTime(i18n, r.created_at)}</time>
                  {canEdit && r.state === 'open' ? (
                    <Btn
                      size="sm"
                      variant="ghost"
                      onClick={() => cancel.mutate(r.id)}
                      disabled={cancel.isPending}
                    >
                      {t('signals.cancel')}
                    </Btn>
                  ) : null}
                </div>
                <p className="pp-why">{what(r)}</p>
                {by ? <p>{t('people.requests.by', { name: by })}</p> : null}
              </li>
            )
          })}
        </ul>
      )}
    </Section>
  )
}
